export const getVersion = async (urlContratos) => {
  const response = await fetch(`${urlContratos}getVersion`, {
    method: "GET",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Servicio de contratos no disponible (${response.status})`);
  }
  return true;
};

export const updateContratoPlazoFirma = async (baseUrl, idContrato, diasFirma) => {
  const queryParams = new URLSearchParams({
    idContrato,
    diasFirma,
  });

  const response = await fetch(
    `${baseUrl}updateContratoPlazoFirma?${queryParams}`,
    { method: "GET" }
  );
  if (!response.ok) {
    throw new Error(`Error al actualizar el plazo de firma (${response.status})`);
  }
  return response.json();
};

// ==================== TIPOS Y ESTADOS DE DOCUMENTO ====================

const normalizarLista = (data, key) =>
  Array.isArray(data?.[key]) ? data[key] : Object.values(data?.[key] || {});

export const getTiposDocumentoByEmpresa = async (urlContratos, empresa) => {
  const response = await fetch(
    `${urlContratos}getTiposDocumentoByEmpresa?empresa=${encodeURIComponent(empresa)}`,
    { method: "GET", credentials: "include" }
  );
  if (!response.ok) {
    throw new Error(`Error al obtener los tipos de documento (${response.status})`);
  }
  const data = await response.json();
  return normalizarLista(data, "tiposDocumentos");
};

export const getAllEstadosDocumento = async (urlContratos) => {
  const response = await fetch(`${urlContratos}getAllEstadosDocumento`, {
    method: "GET",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Error al obtener los estados de documento (${response.status})`);
  }
  const data = await response.json();
  return normalizarLista(data, "estadosDocumentos");
};

// ==================== DOCUMENTOS ====================

export const getAllDocumentos = async (urlContratos) => {
  const response = await fetch(`${urlContratos}getAllDocumentos`, {
    method: "GET",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Error al obtener los documentos (${response.status})`);
  }
  return response.json();
};

export const guardarMetadataDocumento = async (urlContratos, payload) => {
  const response = await fetch(`${urlContratos}guardarMetadataDocumento`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "include",
  });
  if (!response.ok) {
    let detail = "El documento ingreso ya existe, porfavor reingrese tipo o plantilla diferente.";
    try {
      const errData = await response.json();
      detail = errData?.message || detail;
    } catch {
      try {
        const errText = await response.text();
        detail = errText || detail;
      } catch {}
    }
    const error = new Error(detail);
    error.statusCode = response.status;
    throw error;
  }
  return true;
};

export const versionarDocumento = async (urlContratos, payload) => {
  const response = await fetch(`${urlContratos}versionarDocumento`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Error al actualizar documento (${response.status})`);
  }
  return true;
};

export const rescatarDocumento = async (urlContratos, idDocumento) => {
  const response = await fetch(
    `${urlContratos}rescatarDocumento?id=${encodeURIComponent(idDocumento)}`,
    { method: "GET", credentials: "include" }
  );
  if (!response.ok) {
    throw new Error(`Error al rescatar documento (${response.status})`);
  }
  return response.blob();
};

export const rescatarDocumentoPdf = async (urlContratos, idDocumento) => {
  const response = await fetch(
    `${urlContratos}rescatarDocumentoPdf?id=${encodeURIComponent(idDocumento)}`,
    { method: "GET", credentials: "include" }
  );
  if (!response.ok) {
    throw new Error(`Error al rescatar documento (${response.status})`);
  }
  return response.blob();
};

export const anularDocumentoPdf = async (urlContratos, idDocumento, idUsuario) => {
  const response = await fetch(
    `${urlContratos}anularDocumentoPdf?id=${encodeURIComponent(idDocumento)}&idUsuario=${idUsuario}`,
    { method: "PUT", credentials: "include" }
  );
  if (!response.ok) {
    throw new Error(`Error al anular el documento (${response.status})`);
  }
  return true;
};

export const anularDocumento = async (urlContratos, idDocumento, idUsuario) => {
  const response = await fetch(
    `${urlContratos}anularDocumento?id=${encodeURIComponent(idDocumento)}&idUsuario=${idUsuario}`,
    { method: "PUT", credentials: "include" }
  );
  if (!response.ok) {
    throw new Error(`Error al anular el documento (${response.status})`);
  }
  return true;
};

export const guardarPdf = async (urlContratos, formData) => {
  const response = await fetch(`${urlContratos}guardarPdf`, {
    method: "POST",
    body: formData,
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Error al subir documento (${response.status})`);
  }
  return true;
};

export const guardarPdfSinBorrador = async (urlContratos, formData) => {
  const response = await fetch(`${urlContratos}guardarPdfSinBorrador`, {
    method: "POST",
    body: formData,
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Error al subir PDF (${response.status})`);
  }
  return true;
};

// ==================== PLANTILLAS ====================

export const getPlantillasDocumentoByEmpresa = async (urlContratos, empresa, idTipo) => {
  const params = new URLSearchParams();
  if (empresa) params.append("empresa", empresa);
  if (idTipo) params.append("idTipo", idTipo);

  const response = await fetch(`${urlContratos}getPlantillasDocumentoByEmpresa?${params}`, {
    method: "GET",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Error al obtener las plantillas de documento (${response.status})`);
  }
  const data = await response.json();
  return normalizarLista(data, "plantillasDocumentos");
};

export const guardarPlantilla = async (urlContratos, formData) => {
  const response = await fetch(`${urlContratos}guardarPlantilla`, {
    method: "POST",
    body: formData,
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Error al subir plantilla (${response.status})`);
  }
  return true;
};

export const editarPlantillaDocumento = async (
  urlContratos,
  { id, nombre, tipoMime, idAdjunto, idTipoDocumento, idUsuario }
) => {
  const url =
    `${urlContratos}editarPlantillaDocumento` +
    `?id=${encodeURIComponent(id)}` +
    `&nombre=${encodeURIComponent(nombre)}` +
    `&tipoMime=${encodeURIComponent(tipoMime)}` +
    `&idAdjunto=${encodeURIComponent(idAdjunto ?? "")}` +
    `&idTipoDocumento=${encodeURIComponent(idTipoDocumento ?? "")}` +
    `&idUsuario=${encodeURIComponent(idUsuario)}`;

  const response = await fetch(url, {
    method: "PUT",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Error al editar plantilla (${response.status})`);
  }
  return true;
};

export const cambiarEstadoPlantillaDocumento = async (urlContratos, idPlantilla, activo, idUsuario) => {
  const response = await fetch(
    `${urlContratos}cambiarEstadoPlantillaDocumento?idPlantilla=${idPlantilla}&activo=${activo}&idUsuario=${idUsuario}`,
    { method: "PUT", credentials: "include" }
  );
  if (!response.ok) {
    throw new Error(`Error al eliminar plantilla (${response.status})`);
  }
  return true;
};

export const descargarPlantilla = async (urlContratos, id) => {
  const response = await fetch(`${urlContratos}descargarPlantilla?id=${id}`, {
    method: "GET",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Error al descargar plantilla (${response.status})`);
  }
  // Se devuelve el response completo: el consumidor necesita el header content-disposition
  return response;
};

export const sobreescribirPlantilla = async (urlContratos, formData) => {
  const response = await fetch(`${urlContratos}sobreescribirPlantilla`, {
    method: "POST",
    body: formData,
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Error al sobreescribir plantilla (${response.status})`);
  }
  return true;
};
